import React from "react";
import { motion } from "framer-motion";
import { FileText, Stethoscope } from "lucide-react";

const plans = [
  {
    tag: "KARATE",
    jp: "空手",
    name: "Karate Shotokan",
    price: "200",
    unit: "lei / lună",
    detail: "Acces nelimitat la toate ședințele săptămânale: Luni, Miercuri, Joi (18:00 — 19:30).",
    highlight: false,
    testid: "plan-karate",
  },
  {
    tag: "MMA · MIC",
    jp: "総合",
    name: "MMA — 8 ședințe",
    price: "150",
    unit: "lei / lună",
    detail: "8 ședințe la alegere pe parcursul unei luni. Ideal pentru început sau ritm mai relaxat.",
    highlight: false,
    testid: "plan-mma-8",
  },
  {
    tag: "MMA · COMPLET",
    jp: "無差別",
    name: "MMA — 12 ședințe",
    price: "200",
    unit: "lei / lună",
    detail: "12 ședințe pe lună — pentru ritm serios de pregătire. Include toate zilele MMA (20:00 — 21:30).",
    highlight: true,
    testid: "plan-mma-12",
  },
];

const cardV = {
  hidden: { opacity: 0, y: 40 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Pricing = () => {
  return (
    <section
      id="tarife"
      className="section border-t border-[rgba(242,235,229,0.1)]"
      data-testid="pricing-section"
    >
      <div className="container-brutal">
        {/* Header */}
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-24">
          <div className="col-span-12 md:col-span-3">
            <span className="eyebrow">§ 03 / Tarife</span>
          </div>
          <div className="col-span-12 md:col-span-9">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9 }}
              className="section-title"
            >
              Fără trucuri.<br />
              <span className="text-[#D91604]">Doar</span> tarife clare.
            </motion.h2>
            <p className="editorial-lead mt-6 max-w-xl text-[#8a8a8a]">
              Toate abonamentele sunt valabile o lună de la data plății. Prima ședință de
              probă rămâne gratuită, indiferent de plan.
            </p>
          </div>
        </div>

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[rgba(242,235,229,0.1)] border border-[rgba(242,235,229,0.1)]" data-testid="pricing-grid">
          {plans.map((p, i) => (
            <motion.article
              key={p.name}
              custom={i}
              variants={cardV}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className={`relative p-8 md:p-10 flex flex-col justify-between min-h-[420px] transition-colors duration-500 ${
                p.highlight ? "bg-[#D91604] text-[#050505]" : "bg-[#050505] hover:bg-[#0d0d0d]"
              }`}
              data-testid={p.testid}
            >
              {/* Top row */}
              <div>
                <div className="flex items-start justify-between">
                  <span
                    className={`text-[11px] tracking-[0.28em] uppercase ${
                      p.highlight ? "text-[#050505]/70" : "text-[#8a8a8a]"
                    }`}
                  >
                    {p.tag}
                  </span>
                  <span
                    className={`font-jp text-3xl leading-none ${
                      p.highlight ? "text-[#050505]/60" : "text-[#8a8a8a]"
                    }`}
                  >
                    {p.jp}
                  </span>
                </div>
                <h3
                  className={`font-anton text-3xl md:text-4xl mt-8 leading-none uppercase tracking-tight ${
                    p.highlight ? "text-[#050505]" : "text-[#f2ebe5]"
                  }`}
                >
                  {p.name}
                </h3>
                <p
                  className={`font-editorial italic text-lg mt-5 leading-relaxed ${
                    p.highlight ? "text-[#050505]/85" : "text-[#f2ebe5]/75"
                  }`}
                >
                  {p.detail}
                </p>
              </div>

              {/* Price */}
              <div className="mt-10">
                <div
                  className={`h-px w-full mb-6 ${
                    p.highlight ? "bg-[#050505]/25" : "bg-[rgba(242,235,229,0.15)]"
                  }`}
                />
                <div className="flex items-baseline gap-3">
                  <span className="font-anton text-7xl md:text-8xl leading-none tracking-tight">
                    {p.price}
                  </span>
                  <span
                    className={`text-sm tracking-widest uppercase ${
                      p.highlight ? "text-[#050505]/70" : "text-[#8a8a8a]"
                    }`}
                  >
                    {p.unit}
                  </span>
                </div>
              </div>

              {/* Ribbon for highlighted */}
              {p.highlight && (
                <div className="absolute top-6 right-6 flex items-center gap-2 bg-[#050505] text-[#D91604] px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#D91604] animate-pulse" />
                  <span className="text-[10px] tracking-[0.3em] uppercase font-anton">Recomandat</span>
                </div>
              )}
            </motion.article>
          ))}
        </div>

        {/* Enrollment note */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-16 md:mt-20 grid grid-cols-12 gap-6 border border-[rgba(242,235,229,0.1)] p-6 md:p-10"
          data-testid="enrollment-note"
        >
          <div className="col-span-12 md:col-span-1 flex md:justify-center">
            <div className="w-12 h-12 rounded-full border border-[#D91604] flex items-center justify-center">
              <Stethoscope className="w-5 h-5 text-[#D91604]" strokeWidth={1.5} />
            </div>
          </div>
          <div className="col-span-12 md:col-span-11">
            <div className="flex items-center gap-3 mb-3">
              <FileText className="w-4 h-4 text-[#8a8a8a]" strokeWidth={1.5} />
              <span className="eyebrow">Înscriere · Info</span>
            </div>
            <h3 className="font-anton text-2xl md:text-4xl uppercase tracking-tight leading-tight">
              Pentru înscriere ai nevoie doar de{" "}
              <span className="text-[#D91604]">o adeverință de la medic</span>{" "}
              că ești apt pentru sport.
            </h3>
            <p className="font-editorial italic text-lg md:text-xl mt-4 text-[#f2ebe5]/85 leading-relaxed max-w-3xl">
              De restul formalităților de înscriere se ocupă clubul — Elite Center este
              afiliat oficial și îți întocmim toate documentele necesare pentru
              federație și competiții.
            </p>
            <a
              href="tel:0738711661"
              data-testid="enrollment-cta"
              className="inline-flex mt-6 items-center gap-3 font-anton text-sm tracking-widest uppercase px-5 py-3 border border-[rgba(242,235,229,0.22)] hover:bg-[#D91604] hover:text-[#050505] hover:border-[#D91604] transition-colors"
            >
              Sună pentru înscriere → 0738 711 661
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
